import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Film,
  Play,
  ArrowRight,
  ShieldCheck,
  SlidersHorizontal,
  Upload,
  FileVideo,
  Users,
  Subtitles,
  Sparkles,
  Zap,
  CheckCircle2,
  AlertCircle,
  LogIn,
  Loader2,
  Clipboard,
  X,
} from 'lucide-react';
import { YouTubeIcon } from '@/components/icons/YouTubeIcon';
import { useRoomStore } from '@/store/roomStore';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { AuthModal } from '@/components/auth/AuthModal';
import { UserNav } from '@/components/auth/UserNav';
import { PWAInstallButton } from '@/components/common/PWAInstallButton';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { generateRoomId, generateParticipantId, formatBytes } from '@/lib/utils';
import { extractYouTubeVideoId, fetchYouTubeMeta, type YouTubeMeta } from '@/lib/youtube';

export function HomePage() {
  const navigate = useNavigate();
  const { setRoom, setParticipant } = useRoomStore();
  const { user, isAuthenticated, isApprovedHost } = useAuth();

  // Host state
  const [hostName, setHostName] = useState('');
  const [mediaSource, setMediaSource] = useState<'local' | 'youtube'>('local');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileWarning, setFileWarning] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [youtubeInput, setYoutubeInput] = useState('');
  const [youtubeVideoId, setYoutubeVideoId] = useState<string | null>(null);
  const [youtubeMeta, setYoutubeMeta] = useState<YouTubeMeta | null>(null);
  const [youtubeLoading, setYoutubeLoading] = useState(false);
  const [youtubeError, setYoutubeError] = useState<string | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-fill host name when signed in
  useEffect(() => {
    if (user && !hostName) {
      setHostName(user.displayName || user.email?.split('@')[0] || '');
    }
  }, [user, hostName]);

  // Join state
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState<string | null>(null);

  const handleSelectFile = useCallback((file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'mkv' || ext === 'avi') {
      setFileWarning('Tip: MKV/AVI formats depend on your browser. For the best compatibility, MP4 or WebM is recommended.');
    } else {
      setFileWarning(null);
    }
    setSelectedFile(file);
  }, []);

  const handleYouTubeInputChange = useCallback(async (value: string) => {
    setYoutubeInput(value);
    setYoutubeError(null);
    const clean = value.trim();
    if (!clean) {
      setYoutubeVideoId(null);
      setYoutubeMeta(null);
      return;
    }

    const id = extractYouTubeVideoId(clean);
    if (!id) {
      setYoutubeVideoId(null);
      setYoutubeMeta(null);
      setYoutubeError('Please enter a valid YouTube video URL or 11-character video ID.');
      return;
    }

    setYoutubeVideoId(id);
    setYoutubeLoading(true);
    try {
      const meta = await fetchYouTubeMeta(id);
      setYoutubeMeta(meta);
    } catch {
      // fallback
    } finally {
      setYoutubeLoading(false);
    }
  }, []);

  const doCreateRoom = (currentUserUid: string, nameToUse: string, fileToUse: File) => {
    const roomId = generateRoomId();
    const participantId = generateParticipantId();

    setParticipant(participantId, nameToUse.trim(), 'host');
    setRoom({
      roomId,
      hostId: participantId,
      hostUid: currentUserUid,
      mediaType: 'local',
      movieTitle: fileToUse.name.replace(/\.[^/.]+$/, ''),
      movieDuration: 0,
      playbackState: 'paused',
      playbackRate: 1,
      currentTime: 0,
      lastActionTime: Date.now(),
      lastActionBy: participantId,
      version: 0,
      participants: [
        {
          id: participantId,
          name: nameToUse.trim(),
          role: 'host',
          joinedAt: Date.now(),
          connectionState: 'idle',
        },
      ],
      createdAt: Date.now(),
    });

    (window as Window & { __cinesync_file?: File }).__cinesync_file = fileToUse;
    navigate(`/room/${roomId}`);
  };

  const doCreateYouTubeRoom = (currentUserUid: string, nameToUse: string, videoId: string, title: string) => {
    const roomId = generateRoomId();
    const participantId = generateParticipantId();

    setParticipant(participantId, nameToUse.trim(), 'host');
    setRoom({
      roomId,
      hostId: participantId,
      hostUid: currentUserUid,
      mediaType: 'youtube',
      youtubeVideoId: videoId,
      movieTitle: title || `YouTube Video (${videoId})`,
      movieDuration: 0,
      playbackState: 'paused',
      playbackRate: 1,
      currentTime: 0,
      lastActionTime: Date.now(),
      lastActionBy: participantId,
      version: 0,
      participants: [
        {
          id: participantId,
          name: nameToUse.trim(),
          role: 'host',
          joinedAt: Date.now(),
          connectionState: 'idle',
        },
      ],
      createdAt: Date.now(),
    });

    navigate(`/room/${roomId}`);
  };

  const handleQuickCreate = () => {
    if (mediaSource === 'local' && !selectedFile) {
      alert('Please select a video file.');
      return;
    }
    if (mediaSource === 'youtube' && !youtubeVideoId) {
      alert('Please enter a valid YouTube video URL or ID.');
      return;
    }

    if (!isAuthenticated || !user) {
      setIsAuthModalOpen(true);
      return;
    }

    if (!isApprovedHost) {
      alert('Host approval required. Please redeem a valid invitation from the administrator to host watch parties.');
      return;
    }

    const effectiveName = hostName.trim() || user.displayName || user.email?.split('@')[0] || 'Host';
    if (mediaSource === 'local' && selectedFile) {
      doCreateRoom(user.uid, effectiveName, selectedFile);
    } else if (mediaSource === 'youtube' && youtubeVideoId) {
      doCreateYouTubeRoom(user.uid, effectiveName, youtubeVideoId, youtubeMeta?.title || 'YouTube Video');
    }
  };

  const handleQuickJoin = (e: React.FormEvent) => {
    e.preventDefault();
    setJoinError(null);
    const clean = joinCode.trim();
    if (!clean) {
      setJoinError('Please enter a room code or link.');
      return;
    }

    let id = clean;
    if (clean.includes('/room/')) {
      id = clean.split('/room/')[1].split('?')[0].split('/')[0];
    } else if (clean.includes('/join/')) {
      id = clean.split('/join/')[1].split('?')[0].split('/')[0];
    }

    if (id.length < 6) {
      setJoinError('Please enter a valid room code or link.');
      return;
    }
    navigate(`/join/${id}`);
  };

  return (
    <div className="min-h-screen bg-cinema-950 text-cinema-100 selection:bg-lamp/20 selection:text-lamp-bright font-sans">
      {/* Navigation Header */}
      <header className="hairline-b bg-cinema-900/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-cinema-800 border border-cinema-700 flex items-center justify-center text-lamp shadow-sm">
              <Film className="w-5 h-5" />
            </div>
            <div>
              <span className="text-base font-bold tracking-tight text-cinema-100">CineSync</span>
              <span className="hidden sm:inline-block ml-2 text-xs text-cinema-400">
                · Watch Together
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2.5 sm:gap-4 text-sm text-cinema-400">
            <a
              href="#how-it-works"
              className="hover:text-cinema-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp rounded hidden md:block"
            >
              How it works
            </a>
            <a
              href="#features"
              className="hover:text-cinema-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp rounded hidden md:block"
            >
              Features
            </a>
            <PWAInstallButton />
            <ThemeToggle showDropdown />
            <div className="hairline-l h-5 border-cinema-700/80 ml-1 hidden sm:block" />
            <UserNav onOpenAuth={() => setIsAuthModalOpen(true)} />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="max-w-6xl mx-auto px-6 pt-16 pb-20">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Headline and Overview */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-lamp uppercase tracking-wider bg-lamp-dim border border-lamp/20 px-3 py-1 rounded">
              <Sparkles className="w-3.5 h-3.5 text-lamp" />
              <span>No Cloud Upload Needed</span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-cinema-100 leading-tight">
              Watch movies together in perfect sync.
            </h1>

            <p className="text-lg text-cinema-300 max-w-xl leading-relaxed">
              When only one of you has the movie file downloaded, CineSync lets everyone watch it together. Stream directly from your browser to your friends with synchronized pause, play, and seek controls.
            </p>

            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-3 text-sm text-cinema-300">
                <CheckCircle2 className="w-4 h-4 text-reel-emerald flex-shrink-0" />
                <span>No waiting for big files to upload to a cloud server</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-cinema-300">
                <CheckCircle2 className="w-4 h-4 text-reel-emerald flex-shrink-0" />
                <span>Shared controls — anyone in the room can pause or rewind</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-cinema-300">
                <CheckCircle2 className="w-4 h-4 text-reel-emerald flex-shrink-0" />
                <span>Free and private — participants join instantly without accounts</span>
              </div>
            </div>
          </div>

          {/* Right Column: Host & Join Cards */}
          <div className="lg:col-span-5 space-y-6">
            {/* Host a Room Card */}
            <div className="rounded-xl bg-cinema-900 border border-cinema-700 p-6 shadow-cinema-screen relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-lamp"></div>
                  <h2 className="text-base font-bold text-cinema-100 tracking-tight">Host a Watch Party</h2>
                </div>
                <span className="text-xs text-cinema-400">You have the movie</span>
              </div>

              {/* Host Requirement Notice / Authenticated Banner */}
              <div className="mb-4">
                {isAuthenticated && user ? (
                  isApprovedHost ? (
                    <div className="flex items-center gap-2 p-2.5 rounded-lg bg-reel-emerald/10 border border-reel-emerald/20 text-xs text-reel-emerald">
                      <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">
                        Approved Host: <strong className="text-cinema-100 font-medium">{user.displayName || user.email}</strong>
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/25 text-xs text-amber-300">
                      <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="leading-relaxed text-[11px]">
                        <span className="font-semibold text-cinema-100">Host Approval Required: </span>
                        Your account is awaiting an invitation link from the administrator to create rooms.
                      </div>
                    </div>
                  )
                ) : (
                  <div className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-cinema-950/80 border border-cinema-750 text-xs text-cinema-300">
                    <div className="flex items-center gap-2 min-w-0">
                      <ShieldCheck className="w-4 h-4 text-lamp flex-shrink-0" />
                      <span className="truncate">Host account required to start streams</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsAuthModalOpen(true)}
                      className="text-lamp hover:underline font-semibold flex-shrink-0 text-[11px]"
                    >
                      Sign In
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {/* Host Name */}
                <div>
                  <label htmlFor="host-name-input" className="block text-xs font-medium uppercase tracking-wider text-cinema-300 mb-1.5">
                    Your Name
                  </label>
                  <input
                    id="host-name-input"
                    type="text"
                    value={hostName}
                    onChange={(e) => setHostName(e.target.value)}
                    placeholder="e.g. Alex"
                    maxLength={24}
                    className="w-full bg-cinema-950 border border-cinema-700 rounded-md px-3.5 py-2.5 text-sm text-cinema-100 placeholder-cinema-500 focus:outline-none focus:border-lamp focus:ring-1 focus:ring-lamp transition-colors"
                  />
                </div>

                {/* Media Source Selector Tabs */}
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider text-cinema-300 mb-1.5">
                    Media Source
                  </label>
                  <div className="grid grid-cols-2 gap-2 p-1 bg-cinema-950 rounded-lg border border-cinema-700/80">
                    <button
                      type="button"
                      onClick={() => setMediaSource('local')}
                      className={`py-2 px-3 rounded-md text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
                        mediaSource === 'local'
                          ? 'bg-cinema-800 text-cinema-100 shadow-sm border border-cinema-600/60'
                          : 'text-cinema-400 hover:text-cinema-200'
                      }`}
                    >
                      <Film className="w-3.5 h-3.5 text-lamp" />
                      <span>Local Movie</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setMediaSource('youtube')}
                      className={`py-2 px-3 rounded-md text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
                        mediaSource === 'youtube'
                          ? 'bg-cinema-800 text-cinema-100 shadow-sm border border-cinema-600/60'
                          : 'text-cinema-400 hover:text-cinema-200'
                      }`}
                    >
                      <YouTubeIcon className="w-3.5 h-3.5 text-red-500" />
                      <span>YouTube Video</span>
                    </button>
                  </div>
                </div>

                {mediaSource === 'local' ? (
                  /* File Dropzone */
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wider text-cinema-300 mb-1.5">
                      Select Movie File
                    </label>
                    <div
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragging(false);
                        const f = e.dataTransfer.files[0];
                        if (f) handleSelectFile(f);
                      }}
                      onClick={() => fileInputRef.current?.click()}
                      className={`
                        border border-dashed rounded-lg p-5 text-center cursor-pointer transition-all
                        ${isDragging 
                          ? 'border-lamp bg-lamp/10' 
                          : selectedFile 
                          ? 'border-cinema-600 bg-cinema-950/80' 
                          : 'border-cinema-700 bg-cinema-950 hover:border-cinema-600'
                        }
                      `}
                      tabIndex={0}
                      role="button"
                      aria-label="Select movie file"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          fileInputRef.current?.click();
                        }
                      }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".mp4,.webm,.mov,.mkv"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleSelectFile(f);
                        }}
                      />

                      {selectedFile ? (
                        <div className="flex items-center gap-3 text-left">
                          <div className="w-10 h-10 rounded bg-lamp-dim border border-lamp/30 flex items-center justify-center flex-shrink-0 text-lamp">
                            <FileVideo className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-cinema-100 truncate">{selectedFile.name}</p>
                            <p className="text-xs text-cinema-400">{formatBytes(selectedFile.size)} · Ready to stream</p>
                          </div>
                          <span className="text-xs text-lamp hover:underline flex-shrink-0">Change</span>
                        </div>
                      ) : (
                        <div className="py-2 space-y-2">
                          <Upload className="w-6 h-6 text-cinema-400 mx-auto" />
                          <div className="text-xs text-cinema-300">
                            <span className="font-semibold text-cinema-100">Click to select</span> or drag and drop
                          </div>
                          <div className="text-[11px] text-cinema-500">
                            MP4 · WebM · MOV · MKV
                          </div>
                        </div>
                      )}
                    </div>

                    {fileWarning && (
                      <div className="mt-2 text-xs text-amber-300 flex items-start gap-1.5 bg-amber-500/10 border border-amber-500/20 p-2 rounded">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{fileWarning}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  /* YouTube Input */
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label htmlFor="youtube-input" className="block text-xs font-medium uppercase tracking-wider text-cinema-300">
                          YouTube URL or Video ID
                        </label>
                        {youtubeVideoId && (
                          <span className="text-[10px] font-mono text-reel-emerald flex items-center gap-1 font-semibold">
                            <CheckCircle2 className="w-3 h-3" />
                            <span>Ready to stream</span>
                          </span>
                        )}
                      </div>
                      <div className="relative">
                        <input
                          id="youtube-input"
                          type="text"
                          value={youtubeInput}
                          onChange={(e) => handleYouTubeInputChange(e.target.value)}
                          onPaste={(e) => {
                            const pasted = e.clipboardData?.getData('text/plain') || e.clipboardData?.getData('text') || '';
                            if (pasted) {
                              e.preventDefault();
                              handleYouTubeInputChange(pasted.trim());
                            }
                          }}
                          placeholder="Paste YouTube link (watch, youtu.be, shorts, or embed)"
                          className="w-full bg-cinema-950 border border-cinema-700 rounded-md pl-9 pr-24 py-2.5 text-xs text-cinema-100 placeholder-cinema-500 focus:outline-none focus:border-lamp focus:ring-1 focus:ring-lamp transition-colors"
                        />
                        <YouTubeIcon className="w-4 h-4 text-red-500 absolute left-3 top-3 pointer-events-none" />
                        
                        <div className="absolute right-2 top-2 flex items-center gap-1">
                          {youtubeInput ? (
                            <button
                              type="button"
                              onClick={() => handleYouTubeInputChange('')}
                              className="p-1 rounded text-cinema-400 hover:text-cinema-100 hover:bg-cinema-800 transition-colors"
                              title="Clear link"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const text = await navigator.clipboard.readText();
                                  if (text) handleYouTubeInputChange(text.trim());
                                } catch {
                                  setYoutubeError('Clipboard access denied by browser. Please paste using Ctrl+V or Cmd+V.');
                                }
                              }}
                              className="flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-cinema-850 hover:bg-cinema-800 text-cinema-300 hover:text-cinema-100 border border-cinema-700/80 transition-colors shadow-xs"
                              title="Paste from clipboard"
                            >
                              <Clipboard className="w-3 h-3 text-lamp" />
                              <span>Paste</span>
                            </button>
                          )}
                          {youtubeLoading && (
                            <Loader2 className="w-3.5 h-3.5 text-lamp animate-spin ml-1" />
                          )}
                        </div>
                      </div>
                      {youtubeError && (
                        <p className="text-[11px] text-red-400 mt-1.5 flex items-center gap-1 animate-fade-in">
                          <AlertCircle className="w-3 h-3 flex-shrink-0" />
                          <span>{youtubeError}</span>
                        </p>
                      )}
                    </div>

                    {/* YouTube Video Preview Card */}
                    {youtubeMeta && youtubeVideoId && (
                      <div className="p-3 rounded-lg bg-cinema-950 border border-cinema-700/80 flex items-center gap-3 animate-fade-in shadow-xs">
                        <img
                          src={youtubeMeta.thumbnailUrl}
                          alt={youtubeMeta.title}
                          className="w-20 h-12 object-cover rounded bg-cinema-900 flex-shrink-0 border border-cinema-700/60"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-cinema-100 truncate" title={youtubeMeta.title}>
                            {youtubeMeta.title}
                          </p>
                          <p className="text-[11px] text-cinema-400 truncate mt-0.5">
                            {youtubeMeta.authorName} · Official YouTube Player
                          </p>
                        </div>
                      </div>
                    )}

                    <p className="text-[11px] text-cinema-500">
                      Synchronized playback using the official YouTube Player API. No video file download required.
                    </p>
                  </div>
                )}

                {/* Create Button */}
                <button
                  onClick={handleQuickCreate}
                  disabled={
                    (mediaSource === 'local' ? !selectedFile : !youtubeVideoId) ||
                    (isAuthenticated && !isApprovedHost)
                  }
                  className="w-full py-3 px-4 rounded-md bg-lamp hover:bg-lamp-bright disabled:opacity-40 disabled:cursor-not-allowed text-cinema-950 font-bold text-sm tracking-wide transition-all duration-150 enabled:active:scale-[0.96] shadow-md flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
                >
                  {isAuthenticated ? (
                    isApprovedHost ? (
                      <>
                        <Play className="w-4 h-4 fill-current" />
                        <span>Start Watch Party</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4" />
                        <span>Host Approval Required</span>
                      </>
                    )
                  ) : (
                    <>
                      <LogIn className="w-4 h-4" />
                      <span>Sign in to Start Party</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Quick Join Card */}
            <div className="rounded-xl bg-cinema-900 border border-cinema-700/80 p-5 shadow-xs">
              <h3 className="text-sm font-bold text-cinema-100 mb-1.5 flex items-center gap-2">
                <span>Join an Existing Party</span>
              </h3>
              <p className="text-xs text-cinema-400 mb-3">
                Have an invitation link or room code from a friend?
              </p>

              <form onSubmit={handleQuickJoin} className="flex gap-2">
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value)}
                  placeholder="Paste room link or code"
                  aria-label="Room code or link"
                  className="flex-1 bg-cinema-950 border border-cinema-700 rounded-md px-3 py-2 text-xs text-cinema-100 placeholder-cinema-500 focus:outline-none focus:border-lamp focus:ring-1 focus:ring-lamp"
                />
                <button
                  type="submit"
                  className="px-4 py-2 rounded-md bg-cinema-800 hover:bg-cinema-750 border border-cinema-700 text-xs font-semibold text-cinema-200 hover:text-cinema-100 transition-all duration-150 enabled:active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
                >
                  Join
                </button>
              </form>

              {joinError && (
                <p className="text-xs text-red-400 mt-2">{joinError}</p>
              )}
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <section id="how-it-works" className="mt-24 pt-16 hairline-t">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-cinema-100 tracking-tight">
              How it works
            </h2>
            <p className="text-sm text-cinema-400 mt-2">
              Three simple steps to start watching together in under a minute.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            <div className="p-6 rounded-xl bg-cinema-900 border border-cinema-700/60 space-y-3">
              <div className="w-8 h-8 rounded-md bg-lamp-dim border border-lamp/30 flex items-center justify-center text-lamp font-bold text-sm">
                1
              </div>
              <h3 className="text-base font-semibold text-cinema-100">Select your movie</h3>
              <p className="text-xs text-cinema-400 leading-relaxed">
                Choose any movie file from your computer. It starts playing directly on your machine without being uploaded anywhere.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-cinema-900 border border-cinema-700/60 space-y-3">
              <div className="w-8 h-8 rounded-md bg-lamp-dim border border-lamp/30 flex items-center justify-center text-lamp font-bold text-sm">
                2
              </div>
              <h3 className="text-base font-semibold text-cinema-100">Share your private link</h3>
              <p className="text-xs text-cinema-400 leading-relaxed">
                Copy the invitation link and send it to your friends. They simply open it in their browser without downloading any apps.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-cinema-900 border border-cinema-700/60 space-y-3">
              <div className="w-8 h-8 rounded-md bg-lamp-dim border border-lamp/30 flex items-center justify-center text-lamp font-bold text-sm">
                3
              </div>
              <h3 className="text-base font-semibold text-cinema-100">Watch together in sync</h3>
              <p className="text-xs text-cinema-400 leading-relaxed">
                The stream plays for everyone simultaneously. Anyone can pause to grab a drink, rewind, or skip ahead in real time.
              </p>
            </div>
          </div>
        </section>

        {/* Features Highlights */}
        <section id="features" className="mt-20 pt-16 hairline-t">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-cinema-100 tracking-tight">
              Built for movie nights
            </h2>
            <p className="text-sm text-cinema-400 mt-2">
              Everything you need for a comfortable shared viewing experience.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="p-5 rounded-lg bg-cinema-900/60 border border-cinema-700/50 space-y-2">
              <Zap className="w-5 h-5 text-lamp" />
              <h3 className="text-sm font-semibold text-cinema-100">Instant Streaming</h3>
              <p className="text-xs text-cinema-400 leading-relaxed">
                No file uploads or downloads. Your movie streams directly between browsers.
              </p>
            </div>

            <div className="p-5 rounded-lg bg-cinema-900/60 border border-cinema-700/50 space-y-2">
              <SlidersHorizontal className="w-5 h-5 text-lamp" />
              <h3 className="text-sm font-semibold text-cinema-100">Shared Controls</h3>
              <p className="text-xs text-cinema-400 leading-relaxed">
                Both host and guests can pause, play, and seek. Playback stays in lockstep.
              </p>
            </div>

            <div className="p-5 rounded-lg bg-cinema-900/60 border border-cinema-700/50 space-y-2">
              <Subtitles className="w-5 h-5 text-lamp" />
              <h3 className="text-sm font-semibold text-cinema-100">Subtitle Support</h3>
              <p className="text-xs text-cinema-400 leading-relaxed">
                Load external SRT or VTT subtitle files to show synchronized text on all screens.
              </p>
            </div>

            <div className="p-5 rounded-lg bg-cinema-900/60 border border-cinema-700/50 space-y-2">
              <ShieldCheck className="w-5 h-5 text-lamp" />
              <h3 className="text-sm font-semibold text-cinema-100">Private & Secure</h3>
              <p className="text-xs text-cinema-400 leading-relaxed">
                Only people with your private room link can join and watch.
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Simple Footer */}
      <footer className="hairline-t bg-cinema-900/60 py-8 px-6 text-xs text-cinema-400">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Film className="w-4 h-4 text-lamp" />
            <span className="text-cinema-100 font-semibold">CineSync</span>
            <span>— Free browser watch parties</span>
          </div>
          <div>
            <span>No movie file uploads · Open source</span>
          </div>
        </div>
      </footer>

      {/* Host Authentication Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onSuccess={() => {
          setIsAuthModalOpen(false);
          const currentUser = useAuthStore.getState().user;
          if (currentUser && selectedFile) {
            const nameToUse = hostName.trim() || currentUser.displayName || currentUser.email?.split('@')[0] || 'Host';
            doCreateRoom(currentUser.uid, nameToUse, selectedFile);
          }
        }}
      />
    </div>
  );
}
